import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Slider,
  Card,
  CardContent,
  CircularProgress,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Replay10,
  Forward10,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { translations } from '../translations';

interface BookListenerProps {
  audioUrl: string;
  maxChapters: number;
  onClose: () => void;
}

const BookListener: React.FC<BookListenerProps> = ({
  audioUrl,
  maxChapters = 1,
  onClose,
}) => {
  const theme = useTheme();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentChapter, setCurrentChapter] = useState(1);
  const [sliderChanging, setSliderChanging] = useState(false);
  const [direction, setDirection] = useState(document.documentElement.dir);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const t = translations[direction === 'rtl' ? 'ku' : 'en'];

  useEffect(() => {
    if (audioRef.current && audioUrl) {
      const match = audioUrl.match(/^(.+)\.([^.]+)$/);
      if (match) {
        const [, baseName, extension] = match;
        const initialUrl = `${baseName}-01.${extension}`;
        audioRef.current.src = initialUrl;
        audioRef.current.load();
      }
    }
  }, [audioUrl]);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'dir') {
          setDirection(document.documentElement.dir);
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  const handleChapterChange = useCallback((newChapter: number) => {
    if (newChapter >= 1 && newChapter <= maxChapters) {
      setCurrentChapter(newChapter);
      setLoading(true);
      const chapterNumber = newChapter.toString().padStart(2, '0');
      const match = audioUrl.match(/^(.+)\.([^.]+)$/);
      if (match) {
        const [, baseName, extension] = match;
        const newUrl = `${baseName}-${chapterNumber}.${extension}`;
        if (audioRef.current) {
          audioRef.current.src = newUrl;
          audioRef.current.load();
          if (isPlaying) {
            audioRef.current.play();
          }
        }
      }
    }
  }, [maxChapters, audioUrl, isPlaying]);

  useEffect(() => {
    if (!audioUrl) {
      console.error("Audio URL is undefined");
      onClose();
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setLoading(false);
    };

    const handleTimeUpdate = () => {
      if (!sliderChanging) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      if (currentChapter < maxChapters) {
        handleChapterChange(currentChapter + 1);
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioUrl, currentChapter, maxChapters, onClose, sliderChanging, handleChapterChange]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (value: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  const handleSkip = (seconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(audioRef.current.currentTime + seconds, duration));
    }
  };

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: theme.palette.background.default,
    }}>
      <audio ref={audioRef} src={audioUrl} />

      {/* Audio Controls */}
      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', m: 2 }}>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="speed-select-label">{t.speed}</InputLabel>
                  <Select
                    labelId="speed-select-label"
                    value={playbackSpeed}
                    label={t.speed}
                    onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
                  >
                    <MenuItem value={0.5}>0.5x</MenuItem>
                    <MenuItem value={1}>1x</MenuItem>
                    <MenuItem value={1.5}>1.5x</MenuItem>
                    <MenuItem value={2}>2x</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
                <IconButton onClick={() => handleSkip(-10)}>
                  <Replay10 />
                </IconButton>
                <IconButton 
                  onClick={handlePlayPause}
                  sx={{ 
                    mx: 2,
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                    }
                  }}
                >
                  {isPlaying ? <Pause /> : <PlayArrow />}
                </IconButton>
                <IconButton onClick={() => handleSkip(10)}>
                  <Forward10 />
                </IconButton>
              </Box>

              <Box sx={{ px: 2 }}>
                <Slider
                  value={currentTime}
                  min={0}
                  max={duration}
                  onChange={(_, value) => handleSeek(value as number)}
                  onChangeCommitted={(_, value) => {
                    setSliderChanging(false);
                    handleSeek(value as number);
                  }}
                  onMouseDown={() => setSliderChanging(true)}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption">{formatTime(currentTime)}</Typography>
                  <Typography variant="caption">{formatTime(duration)}</Typography>
                </Box>
              </Box>

              {/* Chapter Navigation */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mt: 2 
              }}>
                <IconButton 
                  onClick={() => handleChapterChange(currentChapter - 1)}
                  disabled={currentChapter <= 1}
                >
                  {direction === 'rtl' ? <ChevronRight /> : <ChevronLeft />}
                </IconButton>
                <Typography sx={{ mx: 2 }}>
                  {t.chapter} {currentChapter} {t.of} {maxChapters}
                </Typography>
                <IconButton 
                  onClick={() => handleChapterChange(currentChapter + 1)}
                  disabled={currentChapter >= maxChapters}
                >
                  {direction === 'rtl' ? <ChevronLeft /> : <ChevronRight />}
                </IconButton>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BookListener;
